import { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Box, Modal } from '@mui/material';

interface DownloadPDFModalProps {
  isOpen: boolean;
  handleClose: () => void;
  fileName: string;
  children: React.ReactNode;
  orientation?: 'p' | 'l';
  isSinglePage?: boolean;
  delay?: number;
  padding?: number;
  isPPT?: boolean;
  scale?: number;
  onPDFGenerated?: (pdfFile: File) => void;
}
const getPDFFormat = (isPPT: boolean) => {
  if (isPPT) {
    const pageWidth = 254;
    const pageHeight = (pageWidth * 10) / 16;
    return { pageWidth, pageHeight, format: [pageWidth, pageHeight] };
  }
  return { pageWidth: 210, pageHeight: 297, format: 'a4' };
};
const DownloadPDFModal: React.FC<DownloadPDFModalProps> = ({
  isOpen,
  handleClose,
  fileName,
  children,
  orientation = 'p',
  isSinglePage = false,
  delay = 700,
  padding = 4,
  isPPT = false,
  scale = 1.5,
  onPDFGenerated,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const convertPDFToFile = (pdf: jsPDF, fileName: string): File => {
    const pdfBlob = pdf.output('blob');
    return new File([pdfBlob], fileName, { type: 'application/pdf' });
  };

  const downloadMultiPagePDF = async () => {
    const content = contentRef.current;
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: content.scrollHeight,
    });

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const { pageWidth, pageHeight, format } = getPDFFormat(isPPT);

    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format,
    });
    const margin = 0;
    let yPos = margin;
    let pageY = 0;
    const pageCanvasHeight = (pageHeight - yPos - margin) * (canvas.width / pageWidth);
    let pageCount = 0;

    while (pageY < canvas.height) {
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pageCanvasHeight, canvas.height - pageY);

      const pageCtx = pageCanvas.getContext('2d', { willReadFrequently: true });
      if (pageCtx) {
        pageCtx.drawImage(
          canvas,
          0,
          pageY,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height,
        );

        const imageData = pageCtx.getImageData(0, 0, pageCanvas.width, pageCanvas.height);
        const hasContent = checkIfPageHasContent(imageData);

        if (hasContent) {
          const imgData = pageCanvas.toDataURL('image/jpeg', 0.6);
          pdf.addImage(
            imgData,
            'JPEG',
            margin,
            yPos,
            pageWidth - 2 * margin,
            (pageCanvas.height * (pageWidth - 2 * margin)) / canvas.width,
          );

          pageCount++;

          // Move to next page position
          pageY += pageCanvasHeight;
          pdf.addPage();
        } else {
          // Skip this page if it has no content
          pageY += pageCanvasHeight;
        }
      } else {
        pageY += pageCanvasHeight;
      }
    }

    // Check if there are blank pages and remove them
    if (pdf.getNumberOfPages() > pageCount) {
      const blankPages = pdf.getNumberOfPages() - pageCount;
      for (let i = 0; i < blankPages; i++) {
        pdf.deletePage(pdf.getNumberOfPages());
      }
    }

    if (onPDFGenerated) {
      const pdfFile = convertPDFToFile(pdf, fileName);
      onPDFGenerated(pdfFile);
    } else {
      pdf.save(fileName);
    }
    handleClose();
  };

  const checkIfPageHasContent = (imageData: ImageData): boolean => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    let textPixels = 0;
    let imagePixels = 0;
    const threshold = 50;

    // Sample pixels in a grid pattern
    for (let y = 0; y < height; y += 2) {
      for (let x = 0; x < width; x += 2) {
        const index = (y * width + x) * 4;
        const r = data[index];
        const g = data[index + 1];
        const b = data[index + 2];

        // Check for text (high contrast, dark pixels)
        if (r < 150 && g < 150 && b < 150) {
          textPixels++;
        }

        // Check for colored content
        const avg = (r + g + b) / 3;
        const colorVariation = Math.abs(r - avg) + Math.abs(g - avg) + Math.abs(b - avg);
        if (colorVariation > 20) {
          imagePixels++;
        }

        if (textPixels + imagePixels > threshold) return true;
      }
    }

    const hasContent = textPixels + imagePixels > threshold;

    return hasContent;
  };

  const downloadSinglePagePDF = async () => {
    const content = contentRef.current;
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 1.5,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: content.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 1.0);

    // Calculate PDF dimensions to match the canvas
    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const pageWidth = 210; // A4 width in mm

    // Calculate height to maintain aspect ratio
    const pageHeight = (canvas.height * pageWidth) / canvas.width;

    // Create custom page size to fit content
    pdf.internal.pageSize.height = pageHeight;
    pdf.internal.pageSize.width = pageWidth;

    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);

    if (onPDFGenerated) {
      const pdfFile = convertPDFToFile(pdf, fileName);
      onPDFGenerated(pdfFile);
    } else {
      pdf.save(fileName);
    }
    handleClose();
  };

  useEffect(() => {
    if (isOpen) {
      const timeout = setTimeout(
        isSinglePage ? downloadSinglePagePDF : downloadMultiPagePDF,
        delay,
      );
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={handleClose} hideBackdrop>
      <Box
        ref={contentRef}
        sx={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          transform: 'translate(-50%, -50%)',
          width: 1200,
          bgcolor: '#F0F0F1',
          p: padding,
          boxShadow: 24,
        }}
      >
        {children}
      </Box>
    </Modal>
  );
};

export default DownloadPDFModal;
